import { QueryHookOptions, TypedDocumentNode } from '@apollo/client'
import { Box, InfiniteScroll, Spinner } from '@factorialco/deprecated-design-system'
import { Exact, PaginatedQueryPageInfoFragment } from 'generated/resources/graphql'

import { PAGINATION_KIND } from 'modules/core/components/DataTable/constants'

import { useCallback, useUnsafeCallback } from '../useCallback'
import { useUnsafeMemo } from '../useMemo'
import { UseQueryType, useQuery } from '../useQuery'

export type Controls = {
  fetchMore: () => void
  hasMore: boolean
  loading: boolean
}

/**
 * This type represents the return object of the `usePaginatedQuery` hook.
 * It contains the `loading` and `error` properties returned by the `useQuery` hook, as well as the `nodes` array
 *
 * @typedef {Object} UsePaginatedQueryType
 * @property {boolean} loading Indicates if the query is loading.
 * @property {Error} error The error object returned by the query.
 * @property {Array<Edge['node']> | []} nodes The nodes returned by the query.
 * @property {Query} data The data returned by the query.
 * @property {Controls} controls The controls object to be passed to the paginators.
 */
export type UsePaginatedQueryType<
  Query,
  Variables extends { [key: string]: unknown },
  Edge extends { cursor: string; node: unknown },
> = Pick<UseQueryType<Query, Variables>, 'data' | 'loading' | 'error'> & {
  nodes: Edge['node'][]
  controls: Controls
}

/**
 * This type represents the options object that the `usePaginatedQuery` hook receives.
 *
 * @typedef {Object} PaginatedQueryExtraOptions
 * @property {Function} extractConnection The function that extracts the connection object from the query data.
 */
export type PaginatedQueryExtraOptions<Query, Variables extends Record<string, unknown>, Edge> = {
  extractConnection: (data: NonNullable<UseQueryType<Query, Variables>['data']>) => {
    pageInfo: PaginatedQueryPageInfoFragment
    edges: Array<Edge>
  }
}

/**
 * This hook is a wrapper around the `useQuery` hook and leverages the cursor-based pagination strategy that
 * our backend implements.
 *
 * It works in conjunction with the `InfiniteScrollLoader` and `ButtonLoader` components to provide pagination,
 * but can also work in isolation if the user decides to take a different approach via the `controls` object.
 *
 * It currently only supports cursor-based pagination and it works under the assumption that the GraphQL query accepts,
 * at least, one parameter: `after`. The developer might decide to add more variables to the query, but these
 * two are mandatory.
 *
 * @param {TypedDocumentNode<Query, WithPagination<Variables>>} document The GraphQL document that will be used to fetch the data.
 * @param {PaginatedQueryOptions<Query, Variables, Edge>} options The options object.
 *
 * @returns {UsePaginatedQueryType<Query, Variables, Edge>} The return object.
 */
export const usePaginatedQuery = <
  Query,
  Variables extends { [key: string]: unknown },
  Edge extends { cursor: string; node: unknown },
>(
  document: TypedDocumentNode<Query, Variables>,
  options: Record<string, never> extends Variables
    ? Omit<QueryHookOptions<Query, Variables>, 'variables'> & {
        variables?: Omit<Exact<Variables>, 'after'>
      } & PaginatedQueryExtraOptions<Query, Variables, Edge>
    : Omit<QueryHookOptions<Query, Variables>, 'variables'> & {
        variables: Omit<Exact<Variables>, 'after'>
      } & PaginatedQueryExtraOptions<Query, Variables, Edge>
): UsePaginatedQueryType<Query, Variables, Edge> => {
  const { variables, extractConnection, skip, fetchPolicy } = options

  const paginatedVariables: Exact<Variables> = {
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    ...((variables ?? {}) as Variables),
    after: null,
    __paginationKind: PAGINATION_KIND.CURSOR,
    __paginationInfiniteScroll: true,
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const extractor = useCallback(extractConnection, [])

  const { data, loading, error, fetchMore } = useQuery<Query, Variables>(document, {
    variables: paginatedVariables,
    skip,
    fetchPolicy,
  })

  const connection = useUnsafeMemo(() => (data ? extractor(data) : null), [data, extractor])

  const lastCursor = useUnsafeMemo(() => connection?.pageInfo.endCursor, [connection])

  const fetchMoreCallback = useUnsafeCallback(async () => {
    await fetchMore({
      variables: {
        ...variables,
        after: lastCursor,
      },
    })
  }, [fetchMore, variables, lastCursor])

  const nodes = useUnsafeMemo(() => connection?.edges.map((edge) => edge.node) ?? [], [connection])

  const hasMore = useUnsafeMemo(() => connection?.pageInfo?.hasNextPage ?? false, [connection])

  return {
    loading: !error && !data && !skip,
    error,
    nodes,
    data,
    controls: {
      hasMore: hasMore,
      loading: loading,
      fetchMore: fetchMoreCallback,
    },
  }
}

const DefaultLoader: React.FC = () => (
  // Infinite loader must have a key, because infinite-scroll will concatenate at the end of the options
  <Box key="loader" padding="s16" alignItems="center" justifyContent="center">
    <Spinner />
  </Box>
)

type LoaderProps = {
  controls?: Controls
  children: React.ReactNode
  loader?: React.ReactElement
}

/**
 * This component works in conjunction with the `usePaginatedQuery` hook to provide infinite scrolling functionality.
 * It renders a loader when the query is loading, and triggers the `fetchMore` function when the user scrolls to the bottom of the page.
 *
 * @param {Controls} controls The controls object returned by the `usePaginatedQuery` hook.
 * @param {React.ReactNode} children The children to be rendered.
 * @param {React.ReactElement} loader The loader to be rendered when the query is loading. Defaults to a spinner.
 */
export const InfiniteLoader: React.FC<LoaderProps> = ({
  controls,
  children,
  loader = <DefaultLoader />,
}) => {
  if (!controls) return <>{children}</>

  const { loading, fetchMore, hasMore } = controls

  if (loading) return loader

  return (
    <InfiniteScroll threshold={100} loadMore={fetchMore} hasMore={hasMore} loader={loader}>
      {children}
    </InfiniteScroll>
  )
}
